import { AlertTriangle, Check, Loader2, Phone, RefreshCw, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import PhoneNumberInput from '../components/auth/PhoneNumberInput';
import { storePhoneCountry } from '../components/auth/phoneCountryStorage';
import SumsubVerification from '../components/kyc/SumsubVerification';
import { kycAPI, userAPI } from '../services/api';
import { API_CODES } from '../services/apiCodes';
import { apiErrorCode, getAPILanguage, responseData } from '../services/apiResponse';
import { trackEvent } from '../services/analytics';

const APPROVED = 'approved';
const PENDING = 'pending';
const RETRY = 'retry_required';
const REJECTED = 'rejected';
const PHONE_PATTERN = /^\+[1-9]\d{7,14}$/;

const errorDetail = (error, fallback) =>
  error?.response?.data?.detail || error?.response?.data?.error || error?.message || fallback;

const KYCVerification = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [statusData, setStatusData] = useState({});
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [phoneStatus, setPhoneStatus] = useState('loading');
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [phoneCountry, setPhoneCountry] = useState(null);
  const [phoneChallenge, setPhoneChallenge] = useState(null);
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [phoneCountdown, setPhoneCountdown] = useState(0);

  const successPath = requestId ? `/onramp-checkout/${requestId}` : '/?tab=settings';
  const backPath = requestId ? `/review/${requestId}` : '/?tab=settings';

  const loadStatus = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const response = await kycAPI.getKYCStatus();
      const nextData = responseData(response) || {};
      let nextStatus = nextData.status || 'not_started';
      if (nextStatus === APPROVED && String(nextData.review_answer || '').toUpperCase() !== 'GREEN') {
        nextStatus = 'unavailable';
        setError('Verification status is inconsistent. Please try again.');
      }
      setStatusData(nextData);
      setStatus(nextStatus);
      return nextStatus;
    } catch (requestError) {
      setStatus('unavailable');
      setError(errorDetail(requestError, 'Verification status is temporarily unavailable.'));
      return 'unavailable';
    } finally {
      setBusy(false);
    }
  }, []);

  const loadPhoneIdentity = useCallback(async () => {
    try {
      const response = await userAPI.getIdentities();
      const identities = responseData(response) || {};
      const verifiedPhone = identities.phone?.value && identities.phone?.verified_at;
      setPhoneStatus(verifiedPhone ? 'verified' : 'required');
      return verifiedPhone ? identities.phone.value : '';
    } catch (requestError) {
      setPhoneStatus('unavailable');
      setPhoneError(errorDetail(requestError, 'Could not load your verified phone. Try again.'));
      return '';
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    loadPhoneIdentity();
  }, [loadPhoneIdentity]);

  useEffect(() => {
    if (phoneCountdown <= 0) return undefined;
    const timer = window.setInterval(() => setPhoneCountdown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [phoneCountdown]);

  useEffect(() => {
    if (status === APPROVED && phoneStatus === 'verified' && requestId) {
      navigate(successPath, { replace: true });
    }
  }, [navigate, phoneStatus, requestId, status, successPath]);

  useEffect(() => {
    if (status !== PENDING || session) return undefined;
    const timer = window.setInterval(loadStatus, 5000);
    return () => window.clearInterval(timer);
  }, [loadStatus, session, status]);

  const handlePhoneIdentityRequired = useCallback((requestError) => {
    if (apiErrorCode(requestError) !== API_CODES.PHONE_IDENTITY_REQUIRED) return false;
    setSession(null);
    setPhoneStatus('required');
    setPhoneChallenge(null);
    setPhoneCode('');
    setPhoneCountdown(0);
    setPhoneError(errorDetail(requestError, 'Verify a phone number before continuing.'));
    return true;
  }, []);

  const startVerification = async () => {
    setBusy(true);
    setError('');
    try {
      const response = await kycAPI.startKYC();
      const nextSession = responseData(response);
      if (
        String(nextSession?.provider || '').toUpperCase() !== 'SUMSUB' ||
        nextSession?.presentation !== 'embedded_sdk' ||
        !nextSession?.access_token
      ) {
        throw new Error('Unsupported identity verification provider.');
      }
      setSession(nextSession);
      setStatus(PENDING);
      trackEvent('kyc_start', { provider: 'sumsub' });
    } catch (requestError) {
      if (!handlePhoneIdentityRequired(requestError)) {
        setError(errorDetail(requestError, 'Unable to start identity verification.'));
      }
    } finally {
      setBusy(false);
    }
  };

  const refreshAccessToken = async () => {
    try {
      const response = await kycAPI.refreshKYCToken();
      const token = responseData(response)?.access_token;
      if (!token) throw new Error('Sumsub access token was not returned');
      return token;
    } catch (requestError) {
      handlePhoneIdentityRequired(requestError);
      throw requestError;
    }
  };

  const sdkCompleted = async () => {
    setSession(null);
    trackEvent('kyc_complete', { provider: 'sumsub' });
    await loadStatus();
  };

  const sendPhoneCode = async () => {
    if (!PHONE_PATTERN.test(phoneNumber || '')) {
      setPhoneError('Select a country and enter a valid phone number.');
      return;
    }
    setPhoneBusy(true);
    setPhoneError('');
    try {
      const response = await userAPI.sendIdentityVerificationCode('phone', phoneNumber);
      const challenge = responseData(response) || {};
      if (!challenge.challenge_id) throw new Error('Verification challenge was not returned.');
      setPhoneChallenge(challenge);
      setPhoneCountdown(Number(challenge.resend_after_seconds) || 60);
    } catch (requestError) {
      setPhoneError(errorDetail(requestError, 'Could not send a verification code. Try again.'));
    } finally {
      setPhoneBusy(false);
    }
  };

  const changePhoneNumber = () => {
    setPhoneChallenge(null);
    setPhoneCode('');
    setPhoneCountdown(0);
    setPhoneError('');
  };

  const verifyPhone = async () => {
    if (!/^\d{6}$/.test(phoneCode)) {
      setPhoneError('Enter the 6-digit verification code.');
      return;
    }
    setPhoneBusy(true);
    setPhoneError('');
    try {
      await userAPI.linkIdentity(phoneChallenge.challenge_id, phoneCode);
      const verifiedPhone = await loadPhoneIdentity();
      if (!verifiedPhone) throw new Error('Verified phone was not returned.');
      localStorage.setItem('user_phone', verifiedPhone);
      if (phoneCountry) storePhoneCountry(localStorage, phoneCountry);
      setPhoneChallenge(null);
      setPhoneCode('');
    } catch (requestError) {
      setPhoneError(errorDetail(requestError, 'Could not verify this phone. Try again.'));
    } finally {
      setPhoneBusy(false);
    }
  };

  if (session?.access_token) {
    return (
      <Layout step={4} totalSteps={5} showHome={false} onBack={() => navigate(backPath)}>
        <div className="mb-5 flex items-center gap-3 border-b border-slate-200 pb-4">
          <ShieldCheck className="h-6 w-6 text-cyan-700" aria-hidden="true" />
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Identity verification</h1>
            <p className="mt-1 text-sm text-slate-600">Verification is securely completed by Sumsub.</p>
          </div>
        </div>
        <SumsubVerification
          accessToken={session.access_token}
          refreshAccessToken={refreshAccessToken}
          onComplete={sdkCompleted}
          onError={(sdkError) => setError(sdkError?.message || 'Verification could not be loaded.')}
          lang={getAPILanguage()}
        />
        {error && <p className="mt-4 text-sm text-red-700" role="alert">{error}</p>}
      </Layout>
    );
  }

  const isApproved = status === APPROVED;
  const isPending = status === PENDING;
  const canStart = !['loading', APPROVED, PENDING, REJECTED].includes(status);

  if (phoneStatus !== 'verified') {
    return (
      <Layout step={4} totalSteps={5} onBack={() => navigate(backPath)}>
        <div className="flex items-start gap-4">
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded bg-cyan-50 text-cyan-700">
            <Phone className="h-6 w-6" aria-hidden="true" />
          </span>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Verify your phone</h1>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Add a verified WhatsApp number before continuing identity verification.
            </p>
          </div>
        </div>

        {phoneStatus === 'loading' ? (
          <div className="mt-7 flex h-12 items-center gap-2 text-sm text-slate-600" role="status">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Checking phone
          </div>
        ) : phoneChallenge ? (
          <div className="mt-7 space-y-4">
            <p className="text-sm text-slate-600">Code sent to {phoneChallenge.masked_destination || phoneNumber}.</p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <label className="min-w-0 flex-1">
                <span className="sr-only">Verification code</span>
                <input
                  id="kyc-phone-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={phoneCode}
                  onChange={(event) => setPhoneCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={phoneBusy}
                  placeholder="6-digit code"
                  className="input-field h-12"
                />
              </label>
              <button type="button" onClick={verifyPhone} disabled={phoneBusy} className="btn-primary h-12 shrink-0 sm:min-w-36">
                {phoneBusy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-hidden="true" /> : 'Verify phone'}
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={sendPhoneCode}
                disabled={phoneBusy || phoneCountdown > 0}
                className="text-sm font-medium text-cyan-700 disabled:text-slate-400"
              >
                {phoneCountdown > 0 ? `Resend in ${phoneCountdown}s` : 'Resend code'}
              </button>
              <button type="button" onClick={changePhoneNumber} disabled={phoneBusy} className="text-sm font-medium text-slate-600 hover:text-slate-900">
                Change number
              </button>
            </div>
          </div>
        ) : phoneStatus === 'required' ? (
          <div className="mt-7 space-y-4">
            <PhoneNumberInput
              disabled={phoneBusy}
              onChange={setPhoneNumber}
              onCountryChange={setPhoneCountry}
              invalid={Boolean(phoneError) && !phoneNumber}
              layout="phoneWide"
            />
            <div className="flex justify-end">
              <button type="button" onClick={sendPhoneCode} disabled={phoneBusy} className="btn-primary flex h-12 min-w-36 items-center justify-center gap-2">
                {phoneBusy && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
                Send code
              </button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={loadPhoneIdentity} disabled={phoneBusy} className="btn-primary mt-7 flex h-12 items-center justify-center gap-2 sm:min-w-36">
            <RefreshCw className="h-5 w-5" aria-hidden="true" /> Retry
          </button>
        )}

        {phoneError && <p className="mt-5 text-sm text-red-700" role="alert">{phoneError}</p>}
      </Layout>
    );
  }

  return (
    <Layout step={4} totalSteps={5} onBack={() => navigate(backPath)}>
      <div className="flex items-start gap-4">
        <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded ${
          isApproved ? 'bg-emerald-50 text-emerald-700' : 'bg-cyan-50 text-cyan-700'
        }`}>
          {isApproved ? <Check className="h-6 w-6" aria-hidden="true" /> : <ShieldCheck className="h-6 w-6" aria-hidden="true" />}
        </span>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            {isApproved ? 'Identity verified' : isPending ? 'Verification in review' : 'Identity verification'}
          </h1>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            {isApproved
              ? 'Your Sumsub verification is approved.'
              : isPending
                ? 'Sumsub is reviewing your submission. Approval is required before payment can continue.'
                : 'Complete identity verification with Sumsub before continuing to payment.'}
          </p>
        </div>
      </div>

      {(status === RETRY || status === REJECTED) && (
        <div className="mt-5 flex gap-3 border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <p>{statusData.moderation_comment || (status === RETRY ? 'Sumsub needs additional information. Restart verification to continue.' : 'Verification was not approved. Contact support or restart if Sumsub allows another attempt.')}</p>
        </div>
      )}

      {error && <p className="mt-5 text-sm text-red-700" role="alert">{error}</p>}

      <div className="mt-7 flex flex-col gap-3 sm:flex-row">
        {status === 'loading' ? (
          <div className="flex h-12 items-center justify-center gap-2 text-sm text-slate-600" role="status">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Checking verification
          </div>
        ) : isApproved ? (
          <button type="button" onClick={() => navigate(successPath)} className="btn-primary h-12 flex-1">
            {requestId ? 'Continue to payment' : 'Back to settings'}
          </button>
        ) : isPending ? (
          <button type="button" onClick={loadStatus} disabled={busy} className="btn-primary flex h-12 flex-1 items-center justify-center gap-2">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <RefreshCw className="h-5 w-5" aria-hidden="true" />}
            Check status
          </button>
        ) : canStart ? (
          <button type="button" onClick={startVerification} disabled={busy} className="btn-primary flex h-12 flex-1 items-center justify-center gap-2">
            {busy && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
            {status === RETRY ? 'Restart verification' : 'Start verification'}
          </button>
        ) : null}
      </div>
    </Layout>
  );
};

export default KYCVerification;
